import {GitPullRequestDraftIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {useMarkReadyForReviewMutation} from '@github-ui/mergebox/use-mark-ready-for-review-mutation'
import type {ViewerRole} from '../deciders/use-decide-next-action'

import {ForYouItem} from '../shared/ForYouItem'
import {FailureDialog} from '../../../FailureDialog'
import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {useInvalidatePrState} from '../../../../hooks/use-invalidate-pr-state'
import {PullRequestState} from '../../../../types'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {useHeaderPageData} from '../../../../page-data/loaders/use-header-page-data'

export function shouldShowDraftPR(pullRequestState: PullRequestState) {
  return pullRequestState === PullRequestState.Draft
}

export function DraftPR({userRole}: {userRole?: ViewerRole}) {
  const pullRequestState = usePullRequestState()
  const {user} = useHeaderPageData().data
  const invalidatePrState = useInvalidatePrState()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const markReadyForReviewMutation = useMarkReadyForReviewMutation({
    onSuccess: () => {
      announce('Pull request marked as ready for review')
      invalidatePrState(PullRequestState.Open)
    },
  })

  if (!shouldShowDraftPR(pullRequestState)) {
    return null
  }

  const canMarkReadyForReview = !!user?.canMarkReadyForReview

  if (!canMarkReadyForReview || userRole === 'reviewer') {
    return (
      <ForYouItem
        itemId="draft-pr"
        visual={{kind: 'icon', icon: GitPullRequestDraftIcon, tone: 'neutral', glyphSize: 12}}
        title="Draft"
        subText="This pull request is not ready for review yet."
      />
    )
  }

  const handleMarkReadyForReview = () => {
    if (!canMarkReadyForReview || markReadyForReviewMutation.isPending) return
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: 'draft-pr',
      action_name: 'mark_ready_for_review',
    })
    markReadyForReviewMutation.mutate()
  }

  return (
    <>
      <ForYouItem
        itemId="draft-pr"
        visual={{kind: 'icon', icon: GitPullRequestDraftIcon, tone: 'neutral', glyphSize: 12}}
        title="Draft"
        subText="Mark this pull request as ready when you want reviews."
        actions={[
          {
            id: 'ready-for-review',
            content: (
              <Button
                size="small"
                loading={markReadyForReviewMutation.isPending}
                loadingAnnouncement="Marking ready for review"
                onClick={handleMarkReadyForReview}
              >
                Ready for review
              </Button>
            ),
          },
        ]}
      />
      {markReadyForReviewMutation.isError && (
        <FailureDialog
          title="Failed to mark ready for review"
          message="An error occurred while marking this pull request as ready for review. Please try again."
          details={markReadyForReviewMutation.error.message}
          onRetry={() => markReadyForReviewMutation.mutate()}
          onDismiss={() => markReadyForReviewMutation.reset()}
        />
      )}
    </>
  )
}
